import React, { useState } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import {
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { PAYPAL_CLIENT_ID } from '../../services/config';

/**
 * PayPalPayment - A reusable PayPal Smart Buttons component
 *
 * Props:
 *  - createOrder: async () => orderId  (calls your backend to create a PayPal order)
 *  - onApprove:   async (orderId) => void  (called after buyer approves; calls backend to capture)
 *  - onPaymentComplete: (result) => void  (called on final success)
 *  - amount: number (display only)
 *  - currency: string (display only)
 */
const PayPalPaymentInner = ({
  createOrder,
  onApprove,
  onPaymentComplete,
  amount = 0,
  currency = 'GBP'
}) => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleCreateOrder = async (data, actions) => {
    try {
      setError(null);
      const orderId = await createOrder();
      return orderId;
    } catch (err) {
      setError('Failed to initialize PayPal payment: ' + (err.response?.data?.message || err.message));
      throw err;
    }
  };

  const handleApprove = async (data, actions) => {
    try {
      setProcessing(true);
      setError(null);
      await onApprove(data.orderID);
      onPaymentComplete({ success: true, orderId: data.orderID, provider: 'paypal' });
    } catch (err) {
      setError('Payment approval failed: ' + (err.response?.data?.message || err.message));
      setProcessing(false);
    }
  };

  const handleError = (err) => {
    console.error('PayPal error:', err);
    setError('PayPal encountered an error. Please try again or use a card.');
  };

  const handleCancel = () => {
    setError('Payment was cancelled. Please try again.');
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {processing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
          <CircularProgress size={20} color="success" />
          <Typography variant="body2" color="success.dark" fontWeight={600}>
            Processing payment, please wait...
          </Typography>
        </Box>
      )}

      <PayPalButtons
        style={{
          layout: 'vertical',
          color: 'blue',
          shape: 'rect',
          label: 'pay',
          height: 48
        }}
        disabled={processing}
        forceReRender={[amount, currency]}
        createOrder={handleCreateOrder}
        onApprove={handleApprove}
        onError={handleError}
        onCancel={handleCancel}
      />

      <Typography variant="caption" color="textSecondary" align="center" display="block" sx={{ mt: 2 }}>
        Secured by PayPal. You can pay with your PayPal account or any credit/debit card.
      </Typography>
    </Box>
  );
};

/**
 * PayPalPayment wraps the inner component in a PayPalScriptProvider
 * so PayPal JS SDK is loaded once per mount.
 */
const PayPalPayment = (props) => {
  return (
    <PayPalScriptProvider
      options={{
        'client-id': PAYPAL_CLIENT_ID,
        currency: (props.currency || 'GBP').toUpperCase(),
        intent: 'capture',
        components: 'buttons'
      }}
    >
      <PayPalPaymentInner {...props} />
    </PayPalScriptProvider>
  );
};

export default PayPalPayment;
